import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  input,
} from '@angular/core';
import { Observable, Subject, concat, merge, of, timer } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';

import { AppFacade } from 'ish-core/facades/app.facade';
import { CheckoutFacade } from 'ish-core/facades/checkout.facade';
import { HttpError } from 'ish-core/models/http-error/http-error.model';
import { whenTruthy } from 'ish-core/utils/operators';

import { PresidioMiniCartChromeService } from 'ish-shared/dias/components/custom-mini-basket-content/presidio-mini-cart-chrome.service';

/**
 * CUSTOMIZATION: Presidio mini-basket chrome — accessible trigger (icon + count only),
 * drawer on mobile (not only /basket), open on ATC immediately.
 * Fires GA4 `view_cart` once each time the drawer opens from collapsed.
 * Matches stock close-after-animation; adds Escape, focus restore, and Tab trap.
 */
@Component({
  selector: 'custom-mini-basket',
  // Explicit .presidio.html — webpack theme remap can leave a stale base template.
  templateUrl: './custom-mini-basket.component.presidio.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomMiniBasketComponent implements OnInit, OnDestroy {
  @ViewChild('prsMiniCartTrigger') triggerRef?: ElementRef<HTMLAnchorElement>;
  @ViewChild('prsMiniCartPanel') panelRef?: ElementRef<HTMLElement>;
  @ViewChild('prsMiniCartClose') closeBtnRef?: ElementRef<HTMLButtonElement>;

  basketAnimation$: Observable<string>;
  itemCount$: Observable<number>;
  basketLoading$: Observable<boolean>;

  isCollapsed = true;

  readonly view = input<'auto' | 'small' | 'full'>('auto');

  private basketError$: Observable<HttpError>;
  private destroy$ = new Subject<void>();
  private previouslyFocused: HTMLElement | null = null;

  constructor(
    private checkoutFacade: CheckoutFacade,
    private appFacade: AppFacade,
    private location: Location,
    private cdRef: ChangeDetectorRef,
    private miniCartChrome: PresidioMiniCartChromeService
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    this.itemCount$ = this.checkoutFacade.basketItemCount$;
    this.basketError$ = this.checkoutFacade.basketError$;
    this.basketLoading$ = this.checkoutFacade.basketLoading$;

    // Keep drawer closed on the full cart page (page already shows lines).
    if (this.location.path().startsWith('/basket')) {
      this.collapse();
      this.allowScroll();
    }

    this.basketError$
      .pipe(
        whenTruthy(),
        filter(() => this.location.path() !== '/basket'),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.open());

    // Open immediately on ATC and also on basketChange.
    this.basketAnimation$ = merge(this.miniCartChrome.openRequests$, this.checkoutFacade.basketChange$).pipe(
      filter(() => !this.location.path().startsWith('/basket')),
      switchMap(() => concat(of('mini-basket-animation'), timer(4000).pipe(map(() => ''))))
    );

    this.basketAnimation$.pipe(takeUntil(this.destroy$)).subscribe(animation => {
      if (animation) {
        this.stopScroll();
        this.open();
      } else {
        // Match stock DIAS: release scroll AND close the drawer when the pulse ends.
        this.allowScroll();
        this.collapse();
      }
    });

    this.appFacade.routingInProgress$.pipe(whenTruthy(), takeUntil(this.destroy$)).subscribe(() => {
      this.allowScroll();
      this.collapse();
    });
  }

  toggleCollapse() {
    if (this.isCollapsed) {
      this.stopScroll();
      this.open();
    } else {
      this.allowScroll();
      this.collapse();
    }
  }

  collapse() {
    const wasOpen = !this.isCollapsed;
    this.isCollapsed = true;
    this.cdRef.markForCheck();
    if (wasOpen) {
      this.restoreFocus();
    }
  }

  open() {
    const wasCollapsed = this.isCollapsed;
    this.isCollapsed = false;
    this.cdRef.markForCheck();
    if (wasCollapsed) {
      this.miniCartChrome.trackViewCart();
      this.focusPanel();
    }
  }

  stopScroll() {
    if (!SSR) {
      document.body.style.overflowY = 'hidden';
    }
  }

  allowScroll() {
    if (!SSR) {
      document.body.style.overflowY = 'auto';
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.isCollapsed) {
      this.allowScroll();
      this.collapse();
    }
  }

  /** Keep Tab cycling inside the open dialog panel. */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (this.isCollapsed || event.key !== 'Tab' || SSR) {
      return;
    }
    const panel = this.panelRef?.nativeElement;
    if (!panel) {
      return;
    }
    const focusable = this.getFocusable(panel);
    if (!focusable.length) {
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    } else if (active && !panel.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  }

  private focusPanel(): void {
    if (SSR) {
      return;
    }
    this.previouslyFocused = (document.activeElement as HTMLElement) ?? null;
    // Wait for CD so the close control is focusable (inert cleared).
    queueMicrotask(() => {
      (this.closeBtnRef?.nativeElement ?? this.panelRef?.nativeElement)?.focus?.();
    });
  }

  private restoreFocus(): void {
    if (SSR) {
      return;
    }
    const target = this.previouslyFocused ?? this.triggerRef?.nativeElement ?? null;
    this.previouslyFocused = null;
    queueMicrotask(() => target?.focus?.());
  }

  private getFocusable(root: HTMLElement): HTMLElement[] {
    return Array.from(
      root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => !el.hasAttribute('disabled') && el.tabIndex !== -1 && el.offsetParent !== null);
  }
}
